import {
  endOfDay,
  format,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from "date-fns";

// The list filters on these two keys. They predate the range picker, so a URL
// saved from the old This week / This month / Older buttons still applies.
export const GTE = "meeting_start_time@gte";
export const LTE = "meeting_start_time@lte";

/** ISO instants, as written to the filter. Either side may be open. */
export type DateBounds = { gte?: string; lte?: string };

/** Local calendar days, as shown in the picker. */
export type DayRange = { from?: Date; to?: Date };

export const PRESETS = [
  "today",
  "this_week",
  "this_month",
  "last_30_days",
  "older",
] as const;

export type Preset = (typeof PRESETS)[number];

/**
 * A preset's bounds, in the viewer's time zone. Whole calendar units rather
 * than "until now": no meeting starts in the future, and a fixed end keeps a
 * preset recognisable all week, so the label still says "This week" tomorrow.
 */
export const presetBounds = (preset: Preset, now = new Date()): DateBounds => {
  switch (preset) {
    case "today":
      return rangeToBounds({ from: now, to: now });
    case "this_week":
      return rangeToBounds({ from: startOfWeek(now), to: endOfWeek(now) });
    case "this_month":
      return rangeToBounds({ from: startOfMonth(now), to: endOfMonth(now) });
    case "last_30_days":
      return rangeToBounds({ from: subDays(now, 29), to: now });
    case "older":
      // Everything before this month.
      return rangeToBounds({ to: subDays(startOfMonth(now), 1) });
  }
};

/** Days to bounds: the start of the first day through the end of the last. */
export const rangeToBounds = ({ from, to }: DayRange): DateBounds => ({
  gte: from ? startOfDay(from).toISOString() : undefined,
  lte: to ? endOfDay(to).toISOString() : undefined,
});

/**
 * Bounds back to days. `new Date(iso)` reads an instant, so the day comes out
 * in local time. Only a bare `YYYY-MM-DD` would parse as UTC midnight and shift
 * a day west of Greenwich, and the filter never holds one.
 */
export const boundsToRange = ({ gte, lte }: DateBounds): DayRange => ({
  from: gte ? startOfDay(new Date(gte)) : undefined,
  to: lte ? startOfDay(new Date(lte)) : undefined,
});

/** The preset these bounds were written by, if any. */
export const matchPreset = (
  bounds: DateBounds,
  now = new Date(),
): Preset | null =>
  PRESETS.find((preset) => {
    const p = presetBounds(preset, now);
    return p.gte === bounds.gte && p.lte === bounds.lte;
  }) ?? null;

export const hasBounds = ({ gte, lte }: DateBounds) => !!(gte || lte);

const DATE_LABEL = "resources.zoom_meetings.filters.date";

// "Sep 3", or "Sep 3, 2025" outside the current year.
const day = (d: Date, now: Date) =>
  format(d, d.getFullYear() === now.getFullYear() ? "MMM d" : "MMM d, yyyy");

/**
 * What the date filter reads as: the preset name when the bounds are one,
 * else the days. Shared by the date button and its active-filter chip, so the
 * two always say the same thing.
 */
export const formatDateRange = (
  bounds: DateBounds,
  translate: (key: string, options?: Record<string, unknown>) => string,
  now = new Date(),
): string => {
  const preset = matchPreset(bounds, now);
  if (preset) return translate(`${DATE_LABEL}.${preset}`);
  const { from, to } = boundsToRange(bounds);
  if (from && to) {
    return from.getTime() === to.getTime()
      ? day(from, now)
      : `${day(from, now)} – ${day(to, now)}`;
  }
  if (from) return translate(`${DATE_LABEL}.from`, { date: day(from, now) });
  if (to) return translate(`${DATE_LABEL}.until`, { date: day(to, now) });
  return translate(`${DATE_LABEL}.any`);
};
